import type {
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import type { MouseEvent } from "react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { buildTracesSearchParams } from "../buildTracesSearchParams";
import { formatTraceCost, formatTraceCount, formatTraceDuration } from "../utils";

import { ScrollableTextCell } from "./ScrollableTextCell";
import { TraceInputVariablesCellRenderer } from "./TraceInputVariablesCellRenderer";
import { TraceStatusCellRenderer } from "./TraceStatusCell";
import { SelectionCheckboxCellRenderer, SelectionHeaderRenderer } from "./selectionCells";
import type { TraceGridRow } from "./types";

import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { formatTimestampInZone } from "@/utils/timestamp";
import { createActionsColumn } from "@/components/ui/ag-grid-table/createActionsColumn";
import type { ContextMenuItems } from "@/components/ui/ContextMenu/types";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { Skeleton } from "@/components/ui/skeleton";
import { TableColumn } from "@/components/ui/TableV2/types";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { EntityType } from "@/queries/folder-entities/types";
import type { TraceStructuredFilterGroupRequest } from "@/queries/traces/useTracesSearch";
import { cn } from "@/lib/utils";

const NUMERIC_CELL_CLASS = "text-right tabular-nums text-gray-600";
const NUMERIC_CELL_CONTENT_CLASS = "justify-end text-right";
const NUMERIC_CELL_CONTENT_STYLE = { justifyContent: "end" };
const ENTITY_CELL_CONTENT_CLASS = "items-center justify-start overflow-hidden";
const ENTITY_CELL_SHIMMER_CLASS = "from-gray-100 via-gray-200 to-gray-100";
const ENTITY_CHIP_GAP_PX = 4;
const ENTITY_CHIP_OVERFLOW_WIDTH_PX = 34;
const ENTITY_CHIP_CLASS =
  "inline-flex h-4 min-w-[74px] max-w-[132px] shrink-0 items-center gap-0.5 rounded border bg-white px-1 text-[11px] font-normal leading-none text-gray-600";

type TraceEntityValues = (number | string)[] | null | undefined;
type TraceEntityKind = EntityType.PROMPT | EntityType.WORKFLOW;

interface TraceEntityLinkItem {
  key: string;
  type: TraceEntityKind;
  entityId: string;
  version?: string;
  label: string;
  href: string;
}

interface ParsedVersionRef {
  entityId: string;
  version: string;
}

interface HighlightedTraceEntities {
  promptIds: Set<string>;
  workflowIds: Set<string>;
  promptVersionRefs: Set<string>;
  workflowVersionRefs: Set<string>;
}

interface TraceEntityFilterRequest {
  field: string;
  operator: string;
  value?: unknown;
}

interface TraceEntityFilterGroupRequest {
  filters: unknown[];
}

const toDisplayValues = (values: TraceEntityValues): string[] =>
  values?.map(String).filter(Boolean) ?? [];

const isNumericEntityId = (value: unknown): boolean => {
  if (typeof value === "boolean" || value === null || value === undefined) {
    return false;
  }
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0;
  }

  return /^\d+$/.test(String(value).trim());
};

const hasHydratableIds = (values: TraceEntityValues): boolean =>
  values?.some(isNumericEntityId) ?? false;

const isNegativeOperator = (operator: string): boolean =>
  operator === "not_contains" || operator === "not_in" || operator === "is_not";

const addFilterValues = (target: Set<string>, value: unknown) => {
  if (Array.isArray(value)) {
    value.forEach(item => addFilterValues(target, item));
    return;
  }
  if (value === null || value === undefined || typeof value === "boolean") {
    return;
  }
  target.add(String(value));
};

const isTraceEntityFilterGroupRequest = (
  filterOrGroup: unknown,
): filterOrGroup is TraceEntityFilterGroupRequest =>
  typeof filterOrGroup === "object" &&
  filterOrGroup !== null &&
  "filters" in filterOrGroup &&
  Array.isArray((filterOrGroup as TraceEntityFilterGroupRequest).filters);

const isTraceEntityFilterRequest = (
  filterOrGroup: unknown,
): filterOrGroup is TraceEntityFilterRequest =>
  typeof filterOrGroup === "object" &&
  filterOrGroup !== null &&
  "field" in filterOrGroup &&
  "operator" in filterOrGroup &&
  typeof (filterOrGroup as TraceEntityFilterRequest).field === "string" &&
  typeof (filterOrGroup as TraceEntityFilterRequest).operator === "string";

const collectHighlightedTraceEntities = (
  filterGroup: TraceStructuredFilterGroupRequest | null | undefined,
): HighlightedTraceEntities => {
  const highlighted: HighlightedTraceEntities = {
    promptIds: new Set(),
    workflowIds: new Set(),
    promptVersionRefs: new Set(),
    workflowVersionRefs: new Set(),
  };

  const visit = (filterOrGroup: unknown) => {
    if (isTraceEntityFilterGroupRequest(filterOrGroup)) {
      filterOrGroup.filters.forEach(visit);
      return;
    }

    if (!isTraceEntityFilterRequest(filterOrGroup)) {
      return;
    }

    if (isNegativeOperator(filterOrGroup.operator)) {
      return;
    }

    switch (filterOrGroup.field) {
      case "trace_prompt_ids":
        addFilterValues(highlighted.promptIds, filterOrGroup.value);
        break;
      case "trace_workflow_ids":
        addFilterValues(highlighted.workflowIds, filterOrGroup.value);
        break;
      case "trace_prompt_version_refs":
        addFilterValues(highlighted.promptVersionRefs, filterOrGroup.value);
        break;
      case "trace_workflow_version_refs":
        addFilterValues(highlighted.workflowVersionRefs, filterOrGroup.value);
        break;
      default:
        break;
    }
  };

  filterGroup?.filters.forEach(visit);
  return highlighted;
};

const parseVersionRef = (versionRef: string): ParsedVersionRef | null => {
  const [entityId, version] = versionRef.split(":");
  if (!entityId || !version) {
    return null;
  }
  return { entityId, version };
};

const getEntityBasePath = (workspaceId: number, type: TraceEntityKind, entityId: string): string =>
  type === EntityType.PROMPT
    ? `/workspace/${workspaceId}/prompt/${entityId}`
    : `/workspace/${workspaceId}/workflow/${entityId}`;

const getEntityPath = ({
  workspaceId,
  type,
  entityId,
  version,
}: {
  workspaceId: number;
  type: TraceEntityKind;
  entityId: string;
  version?: string;
}): string => {
  const basePath = getEntityBasePath(workspaceId, type, entityId);
  return version ? `${basePath}/version/${version}` : basePath;
};

const getNameById = ({
  rawValues,
  hydratedValues,
}: {
  rawValues: TraceEntityValues;
  hydratedValues: TraceEntityValues;
}): Map<string, string> => {
  const hydrated = toDisplayValues(hydratedValues);
  return new Map(
    toDisplayValues(rawValues).map((rawValue, index) => [rawValue, hydrated[index] ?? rawValue]),
  );
};

const buildEntityLinkItems = ({
  workspaceId,
  type,
  rawValues,
  hydratedValues,
  versionRefs,
}: {
  workspaceId: number;
  type: TraceEntityKind;
  rawValues: TraceEntityValues;
  hydratedValues: TraceEntityValues;
  versionRefs: string[] | null | undefined;
}): TraceEntityLinkItem[] => {
  const nameById = getNameById({ rawValues, hydratedValues });
  const items: TraceEntityLinkItem[] = [];
  const coveredEntityIds = new Set<string>();

  versionRefs?.forEach(versionRef => {
    const parsed = parseVersionRef(versionRef);
    if (!parsed) {
      return;
    }
    coveredEntityIds.add(parsed.entityId);
    const name = nameById.get(parsed.entityId) ?? parsed.entityId;
    items.push({
      key: `${type}:${parsed.entityId}:${parsed.version}`,
      type,
      entityId: parsed.entityId,
      version: parsed.version,
      label: `${name} v${parsed.version}`,
      href: getEntityPath({
        workspaceId,
        type,
        entityId: parsed.entityId,
        version: parsed.version,
      }),
    });
  });

  toDisplayValues(rawValues).forEach(rawValue => {
    if (coveredEntityIds.has(rawValue)) {
      return;
    }
    items.push({
      key: `${type}:${rawValue}`,
      type,
      entityId: rawValue,
      label: nameById.get(rawValue) ?? rawValue,
      href: getEntityPath({ workspaceId, type, entityId: rawValue }),
    });
  });

  return items;
};

const getTraceEntityItems = (data: TraceGridRow): TraceEntityLinkItem[] => [
  ...buildEntityLinkItems({
    workspaceId: data.workspace_id,
    type: EntityType.PROMPT,
    rawValues: data.prompt_ids,
    hydratedValues: data.hydrated_prompt_names,
    versionRefs: data.prompt_version_refs,
  }),
  ...buildEntityLinkItems({
    workspaceId: data.workspace_id,
    type: EntityType.WORKFLOW,
    rawValues: data.workflow_ids,
    hydratedValues: data.hydrated_workflow_names,
    versionRefs: data.workflow_version_refs,
  }),
];

const getEntityVersionRef = (item: TraceEntityLinkItem): string | null =>
  item.version ? `${item.entityId}:${item.version}` : null;

const isHighlightedEntityItem = (
  item: TraceEntityLinkItem,
  highlightedEntities: HighlightedTraceEntities,
): boolean => {
  if (item.type === EntityType.PROMPT) {
    const versionRef = getEntityVersionRef(item);
    return (
      highlightedEntities.promptIds.has(item.entityId) ||
      (versionRef !== null && highlightedEntities.promptVersionRefs.has(versionRef))
    );
  }

  const versionRef = getEntityVersionRef(item);
  return (
    highlightedEntities.workflowIds.has(item.entityId) ||
    (versionRef !== null && highlightedEntities.workflowVersionRefs.has(versionRef))
  );
};

const sortEntityItemsByHighlight = (
  items: TraceEntityLinkItem[],
  highlightedEntities: HighlightedTraceEntities,
): TraceEntityLinkItem[] =>
  items
    .map((item, index) => ({
      item,
      index,
      isHighlighted: isHighlightedEntityItem(item, highlightedEntities),
    }))
    .sort((left, right) => {
      if (left.isHighlighted !== right.isHighlighted) {
        return left.isHighlighted ? -1 : 1;
      }
      return left.index - right.index;
    })
    .map(({ item }) => item);

const getTraceEntitiesDisplayText = (data?: TraceGridRow): string =>
  data
    ? getTraceEntityItems(data)
        .map(item => item.label)
        .join(", ")
    : "";

const stopCellNavigation = (event: MouseEvent<HTMLAnchorElement>) => {
  event.stopPropagation();
};

const getEntityChipClassName = (isHighlighted: boolean) =>
  cn(
    ENTITY_CHIP_CLASS,
    "hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700",
    isHighlighted ? "border-blue-300 bg-blue-50 text-blue-700 shadow-sm" : "border-gray-200",
  );

const EntityChipContent = ({ item }: { item: TraceEntityLinkItem }) => (
  <>
    <EntityIcon type={item.type} size="xs" className="shrink-0" />
    <span className="truncate">{item.label}</span>
  </>
);

const TruncatedEntityChips = ({
  items,
  highlightedEntities,
}: {
  items: TraceEntityLinkItem[];
  highlightedEntities: HighlightedTraceEntities;
}) => {
  const recalcKey = items.map(item => item.key).join("|");
  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: items.length,
    gapPx: ENTITY_CHIP_GAP_PX,
    overflowIndicatorWidth: ENTITY_CHIP_OVERFLOW_WIDTH_PX,
    keepMinOne: true,
    recalcKey,
  });
  const hiddenItems = items.slice(visibleCount);
  const hiddenTooltip = hiddenItems.map(item => item.label).join(", ");

  return (
    <div
      ref={containerRef}
      className="relative flex w-full min-w-0 max-w-full items-center gap-1 overflow-hidden"
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute left-0 top-0 flex flex-nowrap items-center gap-1"
        aria-hidden="true"
      >
        {items.map(item => (
          <span
            key={item.key}
            className={getEntityChipClassName(isHighlightedEntityItem(item, highlightedEntities))}
          >
            <EntityChipContent item={item} />
          </span>
        ))}
      </div>
      {items.slice(0, visibleCount).map(item => (
        <Link
          key={item.key}
          to={item.href}
          onClick={stopCellNavigation}
          title={item.label}
          className={getEntityChipClassName(isHighlightedEntityItem(item, highlightedEntities))}
        >
          <EntityChipContent item={item} />
        </Link>
      ))}
      {hiddenCount > 0 && (
        <BasicTooltip content={hiddenTooltip}>
          <span className="inline-flex h-4 shrink-0 items-center rounded border border-gray-200 bg-gray-50 px-1 text-[11px] leading-none text-gray-500">
            +{hiddenCount}
          </span>
        </BasicTooltip>
      )}
    </div>
  );
};

const TraceEntitiesCellRenderer = ({
  data,
  highlightedEntities,
}: ICellRendererParams<TraceGridRow> & {
  highlightedEntities: HighlightedTraceEntities;
}) => {
  if (!data) {
    return null;
  }

  const isHydrating =
    data.entity_hydration_status === "pending" &&
    (hasHydratableIds(data.prompt_ids) || hasHydratableIds(data.workflow_ids));
  if (isHydrating) {
    return (
      <div className="flex w-full items-center gap-1.5">
        <Skeleton
          className={`${ENTITY_CELL_SHIMMER_CLASS} shrink-0`}
          width="72px"
          height="10px"
          borderRadius="3px"
        />
        <Skeleton
          className={ENTITY_CELL_SHIMMER_CLASS}
          width="44px"
          height="10px"
          borderRadius="3px"
        />
      </div>
    );
  }

  const items = sortEntityItemsByHighlight(getTraceEntityItems(data), highlightedEntities);
  if (items.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  return <TruncatedEntityChips items={items} highlightedEntities={highlightedEntities} />;
};

export interface TraceColumnsOptions {
  /** Entity analytics pages already scope to one workflow — hide the redundant Trace name. */
  hideTraceName?: boolean;
  /** Prefer Start Time near the left edge on entity analytics tables. */
  prioritizeStartTime?: boolean;
  selectionLimit?: 1;
}

const moveColumnAfterSelect = <T extends { name: string }>(
  columns: T[],
  columnName: string,
): T[] => {
  const column = columns.find(item => item.name === columnName);
  if (!column) {
    return columns;
  }
  const withoutColumn = columns.filter(item => item.name !== columnName);
  const selectIndex = withoutColumn.findIndex(item => item.name === "select");
  const insertAt = selectIndex >= 0 ? selectIndex + 1 : 0;
  return [...withoutColumn.slice(0, insertAt), column, ...withoutColumn.slice(insertAt)];
};

/**
 * AG Grid columns for the traces table. Sortable columns use `field` as the
 * backend sort key (`SORT_FIELDS` in `trace_schema.py`); `valueGetter` reads
 * the projected `TraceListItem` shape returned by the search endpoint.
 */
export const useTraceColumns = (
  timezone?: string,
  contextMenuItems?: ContextMenuItems<TraceGridRow>[],
  options?: TraceColumnsOptions,
): TableColumn<TraceGridRow>[] => {
  const structuredSearchStore = useStructuredSearchStore();
  const tracesSearchParams = buildTracesSearchParams(structuredSearchStore);
  const filterGroupKey = JSON.stringify(tracesSearchParams.filterGroup ?? null);
  const highlightedEntities = useMemo(
    () => collectHighlightedTraceEntities(tracesSearchParams.filterGroup),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterGroupKey],
  );

  return useMemo(() => {
    const hideTraceName = options?.hideTraceName ?? false;
    const prioritizeStartTime = options?.prioritizeStartTime ?? false;

    const columns = [
      {
        id: -1,
        name: "select",
        header: "",
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        pinned: "left",
        cellRenderer: SelectionCheckboxCellRenderer,
        cellRendererParams: { selectionLimit: options?.selectionLimit },
        headerComponent: SelectionHeaderRenderer,
        headerComponentParams: { selectionLimit: options?.selectionLimit },
        cellClass:
          "!justify-center [&_.ag-cell-wrapper]:!justify-center [&_.ag-cell-value]:!justify-center",
        headerClass:
          "[&_.ag-cell-label-container]:!justify-center [&_.ag-header-cell-label]:!justify-center [&_.ag-header-cell-comp-wrapper]:!justify-center",
      },
      {
        id: 0,
        name: "trace_name",
        header: "Trace",
        field: "name",
        flex: 2,
        minWidth: 200,
        sortable: false,
        cellClass: "font-medium text-gray-900",
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => {
          if (!data) {
            return "";
          }
          return data.name || "Untitled trace";
        },
      },
      {
        id: 2,
        name: "trace_status",
        header: "Status",
        width: 120,
        minWidth: 100,
        sortable: false,
        cellRenderer: TraceStatusCellRenderer,
      },
      {
        id: 3,
        name: "trace_duration_ms",
        header: "Duration",
        field: "trace_duration_ms",
        width: 110,
        minWidth: 96,
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => data?.duration_ms ?? null,
        valueFormatter: ({ value }: ValueFormatterParams<TraceGridRow, number | null>) =>
          formatTraceDuration(value ?? null),
      },
      {
        id: 4,
        name: "trace_total_cost_usd",
        header: "Cost",
        field: "trace_total_cost_usd",
        width: 110,
        minWidth: 96,
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => data?.total_cost_usd ?? null,
        valueFormatter: ({ value }: ValueFormatterParams<TraceGridRow, number | null>) =>
          formatTraceCost(value ?? null),
      },
      {
        id: 8,
        name: "trace_input_tokens",
        header: "Input Tokens",
        field: "trace_input_tokens",
        width: 110,
        minWidth: 96,
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => data?.input_tokens ?? null,
        valueFormatter: ({ value }: ValueFormatterParams<TraceGridRow, number | null>) =>
          formatTraceCount(value ?? null),
      },
      {
        id: 9,
        name: "trace_output_tokens",
        header: "Output Tokens",
        field: "trace_output_tokens",
        width: 120,
        minWidth: 104,
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => data?.output_tokens ?? null,
        valueFormatter: ({ value }: ValueFormatterParams<TraceGridRow, number | null>) =>
          formatTraceCount(value ?? null),
      },
      {
        id: 11,
        name: "trace_entities",
        header: "Entities",
        field: "hydrated_prompt_names",
        width: 420,
        minWidth: 300,
        sortable: false,
        cellContentClassName: ENTITY_CELL_CONTENT_CLASS,
        cellRenderer: (params: ICellRendererParams<TraceGridRow>) => (
          <TraceEntitiesCellRenderer {...params} highlightedEntities={highlightedEntities} />
        ),
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) =>
          getTraceEntitiesDisplayText(data),
      },
      {
        id: 12,
        name: "trace_input_variables",
        header: "Input Variables",
        field: "input_variables",
        width: 300,
        minWidth: 220,
        sortable: false,
        cellRenderer: TraceInputVariablesCellRenderer,
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) =>
          data?.input_variables ? Object.keys(data.input_variables).join(", ") : "",
      },
      {
        id: 6,
        name: "trace_span_count",
        header: "# of Spans",
        field: "trace_span_count",
        width: 100,
        minWidth: 88,
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => data?.span_count ?? null,
        valueFormatter: ({ value }: ValueFormatterParams<TraceGridRow, number | null>) =>
          formatTraceCount(value ?? null),
      },
      {
        id: 1,
        name: "trace_id",
        header: "Trace ID",
        field: "trace_id",
        flex: 1.4,
        minWidth: 160,
        sortable: false,
        cellClass: "text-gray-500",
        cellContentClassName: "min-w-0 justify-start overflow-hidden",
        cellRenderer: ({ data }: ICellRendererParams<TraceGridRow>) => {
          const traceId = data?.trace_id;
          if (!traceId) {
            return "";
          }
          return (
            <ScrollableTextCell className="font-mono text-sm text-gray-500" axis="x">
              {traceId}
            </ScrollableTextCell>
          );
        },
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => data?.trace_id ?? "",
      },
      {
        id: 7,
        name: "trace_start",
        header: "Start Time",
        field: "trace_start",
        width: 246,
        minWidth: 246,
        sortable: true,
        fitContent: true,
        cellClass: "text-gray-500",
        valueGetter: ({ data }: ValueGetterParams<TraceGridRow>) => data?.start ?? null,
        valueFormatter: ({ value }: ValueFormatterParams<TraceGridRow, string | null>) =>
          value
            ? formatTimestampInZone(value, timezone, {
                includeSeconds: true,
                showRelative: false,
              })
            : "—",
      },
      createActionsColumn<TraceGridRow>(contextMenuItems, 10),
    ] as TableColumn<TraceGridRow>[];

    const visibleColumns = hideTraceName
      ? columns.filter(column => column.name !== "trace_name")
      : columns;

    return prioritizeStartTime
      ? moveColumnAfterSelect(visibleColumns, "trace_start")
      : visibleColumns;
  }, [
    contextMenuItems,
    highlightedEntities,
    options?.hideTraceName,
    options?.prioritizeStartTime,
    options?.selectionLimit,
    timezone,
  ]);
};
